<template>
  <div>
    <v-sheet class="pa-3 pt-0">
      <v-toolbar flat height="80">
        <ExportLinks
          export-key="campaign-participants"
          :filters="{
            campaign_id: campaign.campaign_id,
            q: search || undefined,
            rewarded: showRewarded ? 'true' : undefined,
          }"
        />

        <v-container v-if="campaignType && campaignType.name === 'draw'" fluid>
          <v-checkbox hide-details v-model="showRewarded">
            <template v-slot:label>
              <div>
                <span class="font-weight-bold text-subtitle-1">{{
                  translations.showRewarded[lang]
                }}</span>
              </div>
            </template>
          </v-checkbox>
        </v-container>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="4" class="pt-3">
          <b-search-field v-model="search"></b-search-field>
        </v-col>
      </v-toolbar>
      <v-toolbar flat>
        <v-btn
          class="text-capitalize mr-4"
          color="error"
          :disabled="totalRewardsGiven === 0"
          :loading="loading"
          @click="openConfirmationDialog('resetRewards')"
        >
          {{ translations.resetRewards[lang] }}
          <span class="pl-1" v-if="totalRewardsGiven"> ({{ totalRewardsGiven }})</span>
        </v-btn>

        <v-btn
          class="text-capitalize"
          color="secondary"
          :disabled="remainingRewardsCount === 0"
          :loading="loading"
          @click="openConfirmationDialog('addRemainingRewards')"
        >
          {{ translations.addRemainingRewards[lang] }}
          <span class="pl-1" v-if="remainingRewardsCount"> ({{ remainingRewardsCount }})</span>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :loading="loading"
        :items="participants"
        :footer-props="{
          itemsPerPageOptions: [12],
          showCurrentPage: true,
          pageText: `${page * 12 - 11}-${
            page * 12 <= serverItemsLength ? page * 12 : serverItemsLength
          } ${translations.paginationText[lang]} ${serverItemsLength}`,
        }"
        :page.sync="page"
        :server-items-length="serverItemsLength"
        class="b-outlined rounded-0"
      >
        <template v-slot:item.completed="{ item }">
          <v-icon v-if="item.completed" v-text="icons.mdiCheckCircle" color="green"></v-icon>
          <v-icon v-else v-text="icons.mdiCloseCircle" color="error"></v-icon>
        </template>
        <template v-slot:item.transaction_id="{ item }">
          {{ item.transaction_id || '-' }}
        </template>
        <template v-slot:item.reward="{ item }">
          {{ (item.reward && item.reward.title) || '-' }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip color="secondary" top>
            <template v-slot:activator="{ on }">
              <v-btn class="mr-4" icon v-on="on" @click="openParticipantInfo(item)">
                <v-icon v-text="icons.mdiEye"></v-icon>
              </v-btn>
            </template>

            <span class="font-weight-bold" v-text="translations.additionalInfo[lang]"></span>
          </v-tooltip>
          <v-tooltip color="secondary" top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="isDrawUserConfirmation"
                :disabled="item.reward !== null || remainingRewardsCount === 0"
                class="mr-4"
                icon
                v-on="on"
                @click="openAssignRewardDialog(item)"
              >
                <v-icon v-text="icons.mdiGift"></v-icon>
              </v-btn>
            </template>
            <span class="font-weight-bold" v-text="translations.assignReward[lang]"></span>
          </v-tooltip>
          <v-tooltip color="secondary" top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="isDrawUserConfirmation"
                :disabled="item.reward == null"
                class="mr-4"
                icon
                v-on="on"
                @click="openConfirmationDialog('detachReward', item)"
              >
                <v-icon v-text="icons.mdiGiftOff"></v-icon>
              </v-btn>
            </template>
            <span class="font-weight-bold" v-text="translations.detachReward[lang]"></span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="participantDialog" max-width="800">
      <ParticipantDetails @cancel="participantDialog = false" />
    </v-dialog>
    <v-dialog v-model="confirmationDialog" max-width="500">
      <b-card
        :title="confirmationTitle"
        @cancel="confirmationDialog = false"
        @submit="confirmAction"
        :loading="loading"
        :type="confirmationType"
      >
        <span>{{ confirmationMessage }}</span>
        <v-simple-table v-if="detachingReward" class="mb-4">
          <template v-slot:default>
            <tbody>
              <tr>
                <td>
                  <strong>{{ translations.participantId[lang] }}</strong>
                </td>
                <td>{{ detachingReward.campaign_participant_id }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ translations.reward[lang] }}</strong>
                </td>
                <td>{{ detachingReward.reward.title }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </b-card>
    </v-dialog>

    <v-dialog v-model="assignRewardDialog" max-width="600">
      <b-card
        :title="translations.assignReward[lang]"
        @cancel="closeAssignRewardDialog"
        @submit="assignReward"
        :submit-text="translations.save"
        :cancel-text="translations.cancel"
        :loading="loading"
      >
        <v-simple-table v-if="selectedParticipant" class="mb-4">
          <template v-slot:default>
            <tbody>
              <tr>
                <td>
                  <strong>{{ translations.participantId[lang] }}</strong>
                </td>
                <td>{{ selectedParticipant.campaign_participant_id }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ translations.first_name[lang] }}</strong>
                </td>
                <td>{{ selectedParticipant.first_name || '-' }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ translations.last_name[lang] }}</strong>
                </td>
                <td>{{ selectedParticipant.last_name || '-' }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ translations.mobile[lang] }}</strong>
                </td>
                <td>{{ selectedParticipant.mobile || '-' }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ translations.reward[lang] }}</strong>
                </td>
                <td>
                  <span v-if="selectedParticipant.reward || remainingRewards.length === 0"
                    >{{ (selectedParticipant.reward && selectedParticipant.reward.title) || '-' }}
                  </span>
                  <b-select
                    v-else
                    v-model="selectedReward"
                    :items="remainingRewards"
                    item-value="campaign_participant_reward_id"
                    :label="translations.selectReward[lang]"
                    class="mb-3"
                  >
                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center">
                        <span class="ml-2"
                          >{{ item.reward.title }} ({{ translations.remaining[lang] }}:
                          {{ item.remaining }})</span
                        >
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center">
                        <v-icon v-if="item" v-text="icons.mdiGift"></v-icon>
                        <span class="ml-2">{{ item.reward.title }}</span>
                      </div>
                    </template>
                  </b-select>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </b-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiEye, mdiGift, mdiGiftOff } from '@mdi/js';

import translations from '@/utils/translations/loyaltyPanel/campaigns/viewCampaigns';
import ParticipantDetails from '@/components/loyaltyPanel/viewCampaigns/ParticipantDetails.vue';
import ExportLinks from '@/components/general/ExportLinks.vue';
import debounce from 'lodash/debounce';

import { mapMutations, mapState, mapActions } from 'vuex';
export default {
  name: 'ParticipantsInfo',

  mixins: [translations],

  components: {
    ParticipantDetails,
    ExportLinks,
  },
  data() {
    return {
      icons: {
        mdiEye,
        mdiGift,
        mdiGiftOff,
      },
      participantDialog: false,
      search: '',
      itemsPerPageOptions: [10, 20, 30, -1],
      page: +this.$route.query.page,
      showRewarded: false,
      confirmationDialog: false,
      confirmationTitle: '',
      confirmationMessage: '',
      confirmationType: '',
      confirmationAction: '',
      assignRewardDialog: false,
      selectedParticipant: null,
      selectedReward: null,
      detachingReward: null,
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage', 'serverItemsLength', 'isSuperAdmin', 'superUser']),
    ...mapState('loyaltyPanel/campaigns/viewCampaigns', [
      'campaign',
      'campaignTypes',
      'campaignRewards',
      'campaignStatuses',
      'participants',
      'systemNotifications',
      'participantsPagination',
    ]),

    lang() {
      return this.$route.params.lang;
    },

    locale() {
      if (this.lang == 'el') return 'el-GR';
      if (this.lang == 'it') return 'it-IT';
      return 'en-GB';
    },
    campaignId() {
      return this.$route.params.campaign_id;
    },

    headers() {
      return [
        {
          text: this.translations.participantId[this.lang],
          value: 'campaign_participant_id',
        },
        {
          text:
            this.campaignType?.name === 'draw'
              ? this.translations.reward[this.lang]
              : this.translations.transactionId[this.lang],
          value: this.campaignType?.name === 'draw' ? 'reward' : 'transaction_id',
        },
        {
          text: this.translations.mobile[this.lang],
          value: 'mobile',
        },
        {
          text: this.translations.completed[this.lang],
          value: 'completed',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
        },
      ];
    },

    query() {
      let query = '?';

      for (let key in this.$route.query) {
        query += `${key}=${this.$route.query[key]}&`;
      }
      if (this.showRewarded) {
        query += `rewarded=true&`;
      }

      return query.slice(0, query.length - 1);
    },

    campaignType() {
      let type = { name: '', value: {} };
      Object.keys(this.campaignTypes).forEach((key) => {
        let foundType = this.campaignTypes[key]?.campaign_types.find(
          (x) => this.campaign.campaign_type_id == x.campaign_type_id
        );
        if (foundType) {
          type.name = key;
          type.value = foundType;
        }
      });
      return type;
    },

    currentStatus() {
      return (
        this.campaignStatuses.find(
          (x) => x.campaign_status_id == this.campaign?.campaign_status_id
        ) || {
          name: {
            el: 'Υπό εξέταση',
            en: 'Under review',
            it: 'In fase di revisione',
          },
        }
      );
    },

    isDrawUserConfirmation() {
      return (
        this.campaignType &&
        this.campaignType.name === 'draw' &&
        this.currentStatus.system == 'user_confirmation'
      );
    },

    remainingRewards() {
      return this.campaignRewards.filter((reward) => reward.remaining > 0);
    },
    remainingRewardsCount() {
      return this.remainingRewards.reduce((acc, reward) => acc + reward.remaining, 0);
    },
    totalRewardsCount() {
      return this.campaignRewards.reduce((acc, reward) => acc + reward.count, 0);
    },
    totalRewardsGiven() {
      return this.totalRewardsCount - this.remainingRewardsCount;
    },
  },
  methods: {
    ...mapActions('loyaltyPanel/campaigns/viewCampaigns', [
      'getParticipants',
      'getParticipant',
      'refreshReward',
      'getCampaignTypes',
      'getCampaignStatuses',
      'resetRewards',
      'addRemainingRewards',
      'assignRewardToParticipant',
      'getCampaignRewards',
      'detachReward',
    ]),
    ...mapActions('loyaltyPanel/customers', ['getCustomerProfile']),
    ...mapMutations(['setDialog', 'setNotification']),

    localizeDate(date, short) {
      return date
        ? new Date(date).toLocaleString(this.locale, {
            weekday: short ? undefined : 'long',
            year: 'numeric',
            month: short ? 'numeric' : 'long',
            day: 'numeric',
          })
        : '-';
    },

    async openParticipantInfo(data) {
      await this.getParticipant({
        campaign_id: this.campaignId,
        participant_id: data.campaign_participant_id,
      });
      this.participantDialog = true;
      await this.getCustomerProfile({ id: data.user_account_id, mode: 'update' });
    },

    async refreshParticipantReward(id) {
      let success = await this.refreshReward({
        campaign_id: this.campaignId,
        participant_id: id,
      });
      if (success) {
        await this.getParticipants({
          query: this.query,
          id: this.campaignId,
        });
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.refreshRewardSuccess[this.lang],
        });
      }
    },
    handleSearch() {
      if (this.page != 1) {
        this.page = 1;
      } else {
        this.getParticipants({
          query: `?q=${this.search}`,
          id: this.campaignId,
        });
      }
    },

    fetchData() {
      this.getParticipants({
        query: this.query,
        id: this.campaignId,
      });

      if (!this.campaignStatuses?.length) {
        this.getCampaignStatuses();
      }

      if (!Object.keys(this.campaignTypes).length) {
        this.getCampaignTypes();
      }
    },

    openConfirmationDialog(action, item = null) {
      this.confirmationTitle = this.translations[action][this.lang];
      this.confirmationMessage = this.translations[`${action}Confirmation`][this.lang];
      this.confirmationAction = action;
      this.confirmationType = action !== 'addRemainingRewards' ? 'delete' : '';
      this.detachingReward = item;
      this.confirmationDialog = true;
    },

    async confirmAction() {
      this.confirmationDialog = false;
      const payload = { campaign_id: this.campaignId };
      if (this.confirmationAction === 'detachReward') {
        payload.campaign_participant_id = this.detachingReward.campaign_participant_id;
      }
      const res = await this[this.confirmationAction](payload);
      if (res && !res.error) {
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations[`${this.confirmationAction}Success`][this.lang],
        });
        this.getCampaignRewards({ campaign_id: this.campaignId });
        this.fetchData();
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: res.message,
        });
      }
    },

    async openAssignRewardDialog(participant) {
      this.selectedParticipant = participant;
      this.selectedReward = null;
      this.assignRewardDialog = true;
    },
    async closeAssignRewardDialog(participant) {
      this.selectedParticipant = null;
      this.selectedReward = null;
      this.assignRewardDialog = false;
    },

    async assignReward() {
      let res = await this.assignRewardToParticipant({
        campaign_id: this.campaignId,
        campaign_participant_id: this.selectedParticipant.campaign_participant_id,
        campaign_participant_reward_id: this.selectedReward,
      });
      if (res && !res.error) {
        this.assignRewardDialog = false;
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.assignRewardSuccess[this.lang],
        });
        this.getCampaignRewards({ campaign_id: this.campaignId });
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: res.message,
        });
      }
    },
  },

  watch: {
    $route(val) {
      if (!val.query.page) {
        this.$router.replace({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
        return;
      }
      this.fetchData();
    },

    page(page) {
      this.$router.replace({ query: { ...this.$route.query, page } });
    },

    search(val) {
      if (val == null) {
        this.getParticipants({
          query: this.query,
          id: this.campaignId,
        });
      } else {
        this.debouncedSearch();
      }
    },

    showRewarded(val) {
      this.page = 1;
      this.getParticipants({
        query: this.query,
        id: this.campaignId,
      });
    },

    campaignId(val) {
      this.getCampaignRewards({ campaign_id: val });
    },
  },
  mounted() {
    this.getCampaignRewards({ campaign_id: this.campaignId });
    this.fetchData();
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },

  beforeCreate() {
    if (!this.$route.query.page) {
      this.$router.replace({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },
};
</script>
<style>
.mw-inh {
  max-width: 100%;
  max-height: 500px;
  overflow: auto;
}
.mw-inh .u-row {
  max-width: 100% !important;
}
.mw-inh .u-row .u-col.u-col-100 {
  max-width: 100% !important;
  min-width: 320px !important;
}
</style>
